/**
 * Short date strings for velocity / progress charts (x-axis).
 * All helpers stay within a single calendar month so the chart x-axis
 * reads as a within-month date range (e.g. "Mar 1", "Mar 8", "Mar 15", "Mar 22").
 * Uses local calendar dates at noon to reduce DST edge cases.
 */

function noon(d) {
    const x = new Date(d);
    x.setHours(12, 0, 0, 0);
    return x;
}

/** e.g. "Mar 24" */
export function formatVelocityAxisDate(date) {
    const d = noon(date instanceof Date ? date : new Date(date));
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/**
 * `count` evenly-spaced date labels from the 1st of `refDate`'s month up
 * to the last day of that month (or today if `refDate` is the current month).
 *
 * All ticks stay within one calendar month, e.g.:
 *   velocityWithinMonthLabels(5) → ["Mar 1","Mar 8","Mar 15","Mar 22","Mar 29"]
 */
export function velocityWithinMonthLabels(count = 5, refDate = new Date()) {
    const ref = noon(refDate);
    const n = Math.max(2, Math.floor(Number(count)) || 5);
    const firstDay = new Date(ref.getFullYear(), ref.getMonth(), 1);
    const today = noon(new Date());
    const isCurrentMonth =
        ref.getFullYear() === today.getFullYear() && ref.getMonth() === today.getMonth();
    const lastDay = isCurrentMonth
        ? today
        : new Date(ref.getFullYear(), ref.getMonth() + 1, 0);
    const rangeMs = lastDay - firstDay;
    const labels = [];
    for (let i = 0; i < n; i++) {
        const d = new Date(firstDay.getTime() + (rangeMs * i) / Math.max(1, n - 1));
        labels.push(formatVelocityAxisDate(d));
    }
    return labels;
}

/**
 * Oldest → newest: `count` weekly ticks all within the current month.
 * Alias of velocityWithinMonthLabels kept for call-sites that asked for "weekly" cadence.
 */
export function velocityWeeklyLabels(count, refDate = new Date()) {
    return velocityWithinMonthLabels(count, refDate);
}

/**
 * `count` evenly-spaced ticks within the current month (same as velocityWithinMonthLabels).
 * Replaces the old multi-month monthly labels.
 */
export function velocityMonthlyLabels(count, refDate = new Date()) {
    return velocityWithinMonthLabels(count, refDate);
}
